import React, { useEffect, useRef, useState } from "react";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { logPodcastAction } from "../../helpers/AnalyticsTags";
import "./PodcastPageV2.css";

/* ------------------------------------------------------------------ */
/*  CONSTANTS                                                         */
/* ------------------------------------------------------------------ */
const AUDIO_PATH = "Podcast media/OFSC Top 5 steps_v7.wav";

/* jump-to chapters (seconds) ------------------------------------------------- */
const CHAPTERS = [
  {
    title: "Introduction",
    start: 0,
    desc: "Five steps for Wildfire Preparedness.",
    cta: null,
    image: "oakland_firesafe_davinci_compressed.jpg",
    defaultImage: true
  },
  {
    title: "Step 1 – Register for AC Alerts",
    start: 26,
    desc: "Sign up for Alameda County’s official emergency alert system to stay informed.",
    cta: { text: "Register for AC Alerts", url: "https://www.alamedacountyca.gov/emergencysite/emergency-site.htm" },
    image: "ac_alert_davinci_compressed.jpg"
  },
  {
    title: "Step 2 – Know Your Zones",
    start: 55,
    desc: "Locate & memorise your evacuation zones to leave quickly when asked.",
    cta: { text: "Know your Zone here", url: "https://protect.genasys.com/search" },
    image: "know_your_zone_davinci_compressed.jpg"
  },
  {
    title: "Step 3 – Family Emergency Plan",
    start: 83,
    desc: "Create and practice a family evacuation & communications plan.",
    cta: { text: "Family Emergency Plan Guide", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-05-Emergency-Planning.pdf" },
    image: "family_wildfire_meetup_compressed.jpg"
  },
  
  {
    title: "Step 4 – Go-Bags",
    start: 114,
    desc: "Prepare emergency go-bags with supplies for at least 72 hours.",
    cta: { text: "Go-bag guide", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-07-Evacuation-and-Go-Bag-Checklists.pdf" },
    image: "wildfire_go_bag_davinci_compressed.jpg"
  },
  {
    title: "Step 5 – Help Your Community",
    start: 148,
    desc: "Check on neighbours, share info, and volunteer with local response groups.",
    cta: { text: "Help your community", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-01-Community-Organization.pdf" },
    image: 'community_wildfire_evac_compressed.jpg'
  },
  {
    title: "Conclusion",
    start: 174,
    desc: "Check on neighbours, share info, and volunteer with local response groups.",
    cta: { text: "OFSC website", url: "https://oaklandfiresafecouncil.org/" },
  }
];

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                         */
/* ------------------------------------------------------------------ */
export default function PodcastPageV2() {
  const storage  = getStorage();
  const db       = getFirestore();
  const audioRef = useRef(null);

  const [audioSrc, setAudioSrc]       = useState("");
  const [duration, setDuration]       = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [chapterIdx, setChapterIdx]   = useState(0);
  const [isPlaying, setIsPlaying]     = useState(false);
  const [subscribed, setSubscribed]   = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  /* handy aliases for current chapter */
  const chapter      = CHAPTERS[chapterIdx];
  const chapterStart = chapter.start;
  const chapterEnd   = chapterIdx + 1 < CHAPTERS.length ? CHAPTERS[chapterIdx + 1].start : duration;
  const chapterLen   = chapterEnd - chapterStart;

  /* helpers ----------------------------------------------------------- */
  const fmt = (t = 0) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };
  const chapterProg = Math.min(Math.max(currentTime - chapterStart, 0), chapterLen);
  const pct         = chapterLen ? (chapterProg / chapterLen) * 100 : 0;
  const stopBubble = (e) => e.stopPropagation();


    const defaultImageName              = CHAPTERS.find(c => c.defaultImage)?.image;
    const [imgUrls, setImgUrls]         = useState({});

    useEffect(() => {
    const loadImgs = async () => {
    const urls = {};
    if (defaultImageName) {
            try {
            urls[defaultImageName] = await getDownloadURL(
                storageRef(storage, `Podcast_images/OFSC_five_steps/${defaultImageName}`)
            );
            } catch (e) { console.error(e); }
        }
        await Promise.all(
            CHAPTERS
            .map(c => c.image)
            .filter(n => n && n !== defaultImageName)
            .map(async n => {
                try {
                urls[n] = await getDownloadURL(
                    storageRef(storage, `Podcast_images/OFSC_five_steps/${n}`)
                );
                } catch (e) { console.error(e); }
            })
        );
        setImgUrls(urls);
        };
        loadImgs();  
    }, [storage]);

    const currentImg =
        (chapter.image && imgUrls[chapter.image]) ||
        imgUrls[defaultImageName] ||
        "";

  /* fetch audio once -------------------------------------------------- */
  useEffect(() => {
    getDownloadURL(storageRef(storage, AUDIO_PATH))
      .then(setAudioSrc)
      .catch((err) => console.error("Audio fetch error:", err));
  }, [storage]);

  useEffect(() => {
      const onFirstPointer = () => setHasInteracted(true);
      window.addEventListener("pointerdown", onFirstPointer, { once: true, capture: true });
      return () => window.removeEventListener("pointerdown", onFirstPointer, { capture: true });
    }, []);

  /* bind audio events ------------------------------------------------- */
  useEffect(() => {
    if (!audioRef.current) return;
    const a = audioRef.current;

    const onLoaded = () => {
      setDuration(a.duration);
      a.currentTime = CHAPTERS[0].start;
      a.play().then(() => setIsPlaying(true)).catch((e) => {console.log(e)});
    };
    const onTime = () => {
      setCurrentTime(a.currentTime);
      if (a.currentTime >= chapterEnd - 0.25 && chapterIdx + 1 < CHAPTERS.length) {

        logPodcastAction({
          platform: "Mobile Web",
          action  : "Auto chapter",
          cta     : "Autoplay",
          chapter : CHAPTERS[chapterIdx+1].title,
          podcast : "OFSC - Five steps"
        });
        setChapterIdx((i) => i + 1);
        a.currentTime = CHAPTERS[chapterIdx + 1].start;

      }
    };

    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
    };
  }, [audioSrc, chapterIdx, chapterEnd]);

  /* controls ---------------------------------------------------------- */
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else           audioRef.current.play();

    logPodcastAction({
      platform: "Mobile Web",
      action  : "Play/Pause",
      cta     : isPlaying ? "Pause" : "Play",
      chapter : chapter.title,
      podcast: "OFSC - Five steps"
      });
    setIsPlaying(!isPlaying);
  };

  const nextChapter = () => {
    if (chapterIdx + 1 >= CHAPTERS.length || !audioRef.current) return;

    logPodcastAction({
      platform: "Mobile Web",
      action  : "Chapter change",
      cta     : "Next",
      chapter : chapter.title,
      podcast: "OFSC - Five steps"
      });

    setChapterIdx(chapterIdx + 1);
    audioRef.current.currentTime = CHAPTERS[chapterIdx + 1].start;
  };

  const prevChapter = () => {
    if (chapterIdx === 0 || !audioRef.current) return;

    logPodcastAction({
      platform: "Mobile Web",
      action  : "Chapter change",
      cta     : "Back",
      chapter : chapter.title,
      podcast: "OFSC - Five steps"
      });

    setChapterIdx(chapterIdx - 1);
    audioRef.current.currentTime = CHAPTERS[chapterIdx - 1].start;
  };

  /* subscribe action -------------------------------------------------- */
  const handleSubscribe = async () => {
    if (subscribed) return;
    try {
      await addDoc(collection(db, "podcast_subscribers"), {
        createdAt: serverTimestamp(),
        topic: "OFSC",
        source: "in-app bell",
      });
      setSubscribed(true);
    } catch (err) {
      console.error(err);
    }
  };

  /* ------------------------------------------------------------------ */
  return (
    <div 
        className="podv2-container"
        style={{ backgroundImage: `url(${currentImg})` }}>
      <div className="podv2-backdrop" />

      {!isPlaying && (
        <button
            className="play-overlay"
            onClick={togglePlay}
            aria-label="Play"
        >
            ►
        </button>
        )}

      {/* right-hand floating rail */}
      {hasInteracted && (
      <div className="podv2-rail">
        <button
          className="icon-btn share-btn"
          title="Share"
          onClick={() =>
            navigator.share?.({
              title: "Wildfire Safety Podcast",
              url: window.location.href,
            })
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
          >
            <path d="M20.3 11.8l-6-5.6a1 1 0 0 0-1.7.7v3.1c-5.9.4-9.3 3.4-10.3 8.6a1 1 0 1 0 2 .4c.9-4.4 3.7-6.4 8.3-6.6v3.1a1 1 0 0 0 1.7.7l6-5.6a1 1 0 0 0 0-1.4z"/>
          </svg>
          <span>Share</span>
        </button>
      </div>
      )}

      {/* chapter copy + optional CTA */}
      <main 
        className="podv2-body" 
        onClick={togglePlay} 
        >

        <h1 
            className="podv2-heading" 
            style={{ opacity: isPlaying ? 0 : 1, transition: "opacity .3s" }}
            onClick={(e) => {}}>
              Wildfire&nbsp;Evacuation&nbsp;Quick&nbsp;Guide
        </h1>

        <p
          className="podv2-author"
          style={{ opacity: isPlaying ? 0 : 0.9, transition: "opacity .3s" }}
        >
          Oakland&nbsp;FireSafe&nbsp;Council
        </p>


        <div
         className="podv2-content"
            style={{ opacity: hasInteracted ? 1 : 0, transition: "opacity .3s" }}
            onClick={stopBubble}     
        >


            <motion.h2
            key={chapterIdx}
            className={chapter.cta ? "" : "no-cta"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            >
            {chapter.title}
            </motion.h2>

           {/* 
            <motion.p
            key={chapterIdx + "_desc"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
            >
            {chapter.desc}
            </motion.p>
            */ }

            {chapter.cta?.text && (
            <motion.a
                key={chapterIdx + "_cta"}
                href={chapter.cta.url}
                target="_blank"
                rel="noreferrer"
                className="cta-btn"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.1 }}
                onClick={(e) => {
                  stopBubble(e);
                 logPodcastAction({
                  platform: "Mobile Web",
                   action  : "CTA",
                   cta     : chapter.cta.text,
                   chapter : chapter.title,
                   podcast: "OFSC - Five steps"
                 });
                }}>
                {chapter.cta.text}
            </motion.a>
            )}
        </div>
      </main>

      {/* bottom player bar */}
      {hasInteracted && (
      <footer className="podv2-controls">
        <button
            className="nav-btn back-btn"
            onClick={(e) => { stopBubble(e); prevChapter(); }}
            disabled={chapterIdx === 0}
            aria-label="Previous section"
        >
            ‹&nbsp;Back
        </button>

        <div
          className="podv2-progress"
          onClick={(e) => {
            const { left, width } = e.currentTarget.getBoundingClientRect();
            const clickPct = (e.clientX - left) / width;
            if (audioRef.current) {
              audioRef.current.currentTime = chapterStart + clickPct * chapterLen;
            }
          }}
        >
          <div className="bar" style={{ width: `${pct}%` }} />
        </div>

        <div className="podv2-timer">
          {fmt(chapterProg)} {/* {fmt(chapterLen)}*/}
        </div>

        {/*
            <button className="play-btn" onClick={togglePlay}>
               {isPlaying ? "❚❚" : "►"}
            </button>
            */
        }

        <button
          className="nav-btn next-btn"
          onClick={(e) => { stopBubble(e); nextChapter(); }}
          disabled={chapterIdx + 1 >= CHAPTERS.length}
          aria-label="Next section"
        >
          Next&nbsp;›
        </button>
      </footer>
      )}

      <audio ref={audioRef} src={audioSrc} preload="metadata" autoPlay />
    </div>
  );
}